import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'ru' | 'ky' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'ky' || saved === 'en' ? saved : 'ru') as Language;
  });

  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTranslations = async () => {
      setIsLoading(true);
      try {
        // Добавляем timestamp для предотвращения кеширования
        const cacheBuster = Date.now();
        const response = await fetch(`/locales/${language}.json?v=${cacheBuster}`);
        const data = await response.json();
        setTranslations(data);
      } catch (error) {
        console.error('Failed to load translations:', error);
        // Fallback на import если fetch не работает
        try {
          const module = await import(`../locales/${language}.json`);
          setTranslations(module.default);
        } catch (importError) {
          console.error('Failed to import translations:', importError);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadTranslations();
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[key] || key;
  };

  if (isLoading && Object.keys(translations).length === 0) {
    return null; // Ждем загрузки переводов при первой инициализации
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
